import React, { useState } from "react";
import { Modal, Paper, Typography, Box, Button, Chip } from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CircularProgress from "@mui/material/CircularProgress";

const SensitiveNameFileUpload = ({
	open,
	onClose,
	onUpload,
	sensitiveKeywords = [],
	currentFolderPath = "",
	companyName = "",
}) => {
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState("");

	const handleFileSelect = (event) => {
		const selectedFile = event.target.files?.[0];
		if (selectedFile) {
			if (selectedFile.name !== "sensitiveNameApproval.pdf") {
				setError(
					"Invalid file name. Please upload a file named 'sensitiveNameApproval.pdf'."
				);
				setFile(null);
			} else {
				setError("");
				setFile(selectedFile);
			}
		}
	};

	const handleClose = () => {
		setFile(null);
		setError("");
		onClose();
	};

	const handleUpload = async () => {
		if (!file) return;

		setUploading(true);
		try {
			const formData = new FormData();
			formData.append("files", file);

			if (currentFolderPath) {
				formData.append("folderPath", currentFolderPath);
			}

			await onUpload(formData);
			handleClose();
		} catch (error) {
			console.error("Upload error:", error);
		} finally {
			setUploading(false);
		}
	};

	return (
		<Modal open={open} onClose={handleClose}>
			<Paper
				sx={{
					width: "50%",
					padding: 3,
					margin: "auto",
					marginTop: "6%",
					outline: "none",
					overflowY: "auto",
					maxHeight: "700px",
				}}>
				<Typography variant='h6' fontWeight='bold'>
					Sensitive Name Approval Document
				</Typography>

				<Typography variant='body1' mt={2}>
					As part of our review process, we have identified the
					following
					{(sensitiveKeywords?.length || 0) > 1
						? " sensitive terms"
						: " sensitive term"}{" "}
					in your proposed company name <strong>{companyName}</strong>
					:
				</Typography>

				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 1,
						mt: 2,
					}}>
					{(sensitiveKeywords || []).map((word, idx) => {
						const formattedWord = word
							.toLowerCase()
							.replace(/^\w/, (c) => c.toUpperCase()); // Capitalize first letter only

						return (
							<Chip
								key={idx}
								label={formattedWord}
								variant='outlined'
								color='warning'
								sx={{ fontWeight: 500 }}
							/>
						);
					})}
				</Box>

				<Typography variant='body2' color='textSecondary' mt={2}>
					To proceed with the company formation, please upload an
					official approval document named exactly{" "}
					<strong>sensitiveNameApproval.pdf</strong>.
				</Typography>

				{/* File Upload Section */}
				<Box
					sx={{
						textAlign: "center",
						marginTop: 3,
						padding: 2,
						backgroundColor: "#f3e8ff",
						borderRadius: 2,
						border: "1px dotted grey",
						height: "293px",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
					}}>
					<label htmlFor='file-upload' style={{ cursor: "pointer" }}>
						<CloudUploadRoundedIcon
							sx={{ fontSize: "100px", color: "#000" }}
						/>
					</label>

					<Typography variant='h6' fontWeight='bold'>
						Select the approval document
					</Typography>

					<Typography variant='subtitle1' color='textSecondary'>
						Click to{" "}
						<label
							htmlFor='file-upload'
							style={{
								color: "blue",
								cursor: "pointer",
								textDecoration: "underline",
							}}>
							browse
						</label>{" "}
						your desktop
					</Typography>

					<input
						type='file'
						id='file-upload'
						style={{ display: "none" }}
						onChange={handleFileSelect}
					/>
				</Box>

				{file && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							padding: "8px 12px",
							borderRadius: "8px",
							border: "1px solid #ddd",
							width: "100%",
							justifyContent: "space-between",
							marginTop: 2,
						}}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
							}}>
							<DescriptionRoundedIcon
								sx={{ color: "blue", fontSize: 34 }}
							/>
							<Typography fontWeight='bold'>
								{file.name}
							</Typography>
							<Typography variant='caption' color='gray'>
								{Math.floor(file.size / 1000)} Kb
							</Typography>
						</Box>

						<CancelIcon
							sx={{
								cursor: "pointer",
								color: "gray",
								fontSize: 25,
							}}
							onClick={() => setFile(null)}
						/>
					</Box>
				)}

				{error && (
					<Typography color='error' mt={2}>
						{error}
					</Typography>
				)}

				<Box
					sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
					<Button
						variant='contained'
						sx={{ backgroundColor: "blue" }}
						startIcon={<FileUploadOutlinedIcon />}
						disabled={!file || !!error}
						onClick={handleUpload}>
						{uploading ? (
							<CircularProgress color='inherit' size='30px' />
						) : (
							"Upload"
						)}
					</Button>
				</Box>
			</Paper>
		</Modal>
	);
};

export default SensitiveNameFileUpload;
